import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, X, ChevronRight, Clock, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { WaitlistDetailResponse } from '../types';
import { waitlistApi } from '../services/api';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import WaitlistPositionBadge from './WaitlistPositionBadge';
import WaitlistBadge from './WaitlistBadge';
import Tooltip from './Tooltip';
import { vibrateLight, vibrateNotification } from '../utils/haptics';
import { playSound } from '../utils/sound';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling
import { parseDateOnly } from '../utils/fairStatus'; // Issue #1672

interface WaitlistCardProps {
  entry: WaitlistDetailResponse;
  onRemoved?: () => void;
  onOfferAction?: () => void;
}

/**
 * WaitlistCard - Display a single waitlist entry in vendor dashboard
 * Shows fair info, position, status, and actions
 *
 * Accessibility (Issue #499):
 * - Respects prefers-reduced-motion
 * - ARIA labels for status updates
 */
function WaitlistCard({ entry, onRemoved, onOfferAction }: WaitlistCardProps) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isRemoving, setIsRemoving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdownRefresh, setCountdownRefresh] = useState(0);
  const [previousPosition, setPreviousPosition] = useState<number | undefined>(undefined);

  // Track position changes using localStorage
  useEffect(() => {
    const storageKey = `waitlist_position_${entry.id}`;
    const storedPosition = localStorage.getItem(storageKey);

    if (storedPosition) {
      const prev = parseInt(storedPosition, 10);
      if (prev !== entry.position) {
        setPreviousPosition(prev);
        // Haptic feedback when position changes (Issue #500)
        vibrateLight();
      }
    }

    // Update stored position
    localStorage.setItem(storageKey, entry.position.toString());
  }, [entry.id, entry.position]);

  // Live countdown timer - triggers re-render every second for countdown updates
  useEffect(() => {
    if (!entry.offer_expires_at || entry.status !== 'notified') return;

    const interval = setInterval(() => {
      setCountdownRefresh(prev => prev + 1);

      // Check if expired and auto-refresh
      const now = new Date();
      const expiresAt = new Date(entry.offer_expires_at!);
      if (now >= expiresAt) {
        onOfferAction?.(); // Trigger refresh
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [entry.offer_expires_at, entry.status, onOfferAction]);

  const handleLeave = useCallback(async () => {
    try {
      setError(null);
      setIsRemoving(true);

      // Haptic feedback for action (Issue #500)
      vibrateLight();

      await waitlistApi.leaveWaitlist(entry.id);

      // Success feedback (Issue #500)
      playSound('success');
      vibrateNotification();
      toast.success('Successfully left the waitlist');
      onRemoved?.();
    } catch (err: unknown) {
      const errorMsg = extractApiError(err).message;
      setError(errorMsg);

      // Error feedback (Issue #500)
      playSound('error');
      toast.error(errorMsg);
    } finally {
      setIsRemoving(false);
      setShowConfirm(false);
    }
  }, [entry.id, onRemoved]);

  const formatDate = useCallback((dateString?: string) => {
    const date = parseDateOnly(dateString); // Issue #1672: handles YYYY-MM-DD as local time
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }, []);

  // Calculate countdown details - recalculates on every render when countdownRefresh changes
  // Removed useMemo to fix regression: countdown needs to update every second (Issue #656)
  const getCountdownDetails = () => {
    if (!entry.offer_expires_at) return null;

    // Reference countdownRefresh to make re-render dependency explicit
    // This updates every second via the interval in the useEffect above
    void countdownRefresh;

    const now = new Date();
    const expiresAt = new Date(entry.offer_expires_at);
    const diff = expiresAt.getTime() - now.getTime();

    if (diff <= 0) {
      return { text: 'Expired', colorClass: 'red', isExpired: true, isUrgent: false };
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    let text: string;
    let colorClass: string;
    let isUrgent = false;

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      text = `${days} day${days > 1 ? 's' : ''} remaining`;
      colorClass = 'green';
    } else if (hours >= 6) {
      text = `${hours}h remaining`;
      colorClass = 'yellow';
    } else if (hours >= 1) {
      text = `${hours}h ${minutes}m remaining`;
      colorClass = 'red';
      isUrgent = true;
    } else {
      text = `${minutes}m remaining!`;
      colorClass = 'red';
      isUrgent = true;
    }

    return { text, colorClass, isExpired: false, isUrgent };
  };

  const countdownDetails = getCountdownDetails();

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {entry.fair_name || 'Unknown Fair'}
          </h3>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4" />
            {formatDate(entry.fair_start_date)} - {formatDate(entry.fair_end_date)}
          </div>
        </div>
        {(entry.status === 'waiting' || entry.status === 'notified') ? (
          <WaitlistPositionBadge
            position={entry.position}
            previousPosition={previousPosition}
            animated={true}
            size="md"
          />
        ) : (
          <WaitlistBadge status={entry.status} position={entry.position} />
        )}
      </div>

      {/* Position Info */}
      {entry.status === 'waiting' && (
        <div className="bg-moonbone border border-teal-200 rounded-md p-3 mb-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-ink">
                Position #{entry.position} in queue
              </p>
              {entry.slot_preference && (
                <p className="text-xs text-primary-dark mt-1">
                  Preference: {entry.slot_preference}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Offer Notification */}
      {entry.status === 'notified' && entry.offer_expires_at && countdownDetails && (
        <div
          className={`
            border rounded-md p-3 mb-3 transition-all duration-300
            ${countdownDetails.colorClass === 'green' ? 'bg-green-50 border-green-200' : ''}
            ${countdownDetails.colorClass === 'yellow' ? 'bg-yellow-50 border-yellow-200' : ''}
            ${countdownDetails.colorClass === 'red' ? 'bg-red-50 border-red-200' : ''}
            ${countdownDetails.isUrgent && !prefersReducedMotion ? 'animate-pulse shadow-md' : ''}
            ${countdownDetails.isUrgent && prefersReducedMotion ? 'shadow-md' : ''}
          `}
          role="alert"
          aria-live="assertive"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <p
                className={`text-sm font-bold mb-1 ${
                  countdownDetails.colorClass === 'green' ? 'text-green-900' :
                  countdownDetails.colorClass === 'yellow' ? 'text-yellow-900' :
                  'text-red-900'
                }`}
              >
                Slot Available!
              </p>
              <Tooltip
                content={
                  countdownDetails.colorClass === 'green'
                    ? 'More than 24 hours remaining - plenty of time!'
                    : countdownDetails.colorClass === 'yellow'
                    ? '6-24 hours remaining - respond soon'
                    : 'Less than 6 hours - urgent action needed!'
                }
                position="top"
              >
                <div className="flex items-center gap-1.5 cursor-help">
                  <Clock
                    className={`w-3.5 h-3.5 ${
                      countdownDetails.colorClass === 'green' ? 'text-green-600' :
                      countdownDetails.colorClass === 'yellow' ? 'text-yellow-600' :
                      'text-red-600'
                    }`}
                  />
                  <p
                    className={`text-xs font-medium ${
                      countdownDetails.colorClass === 'green' ? 'text-green-700' :
                      countdownDetails.colorClass === 'yellow' ? 'text-yellow-700' :
                      'text-red-700'
                    }`}
                  >
                    {countdownDetails.text}
                  </p>
                </div>
              </Tooltip>
            </div>
            <button
              onClick={onOfferAction}
              className={`px-4 py-2 text-white text-sm font-medium rounded-md transition-all flex items-center gap-1 shadow-sm hover:shadow-md whitespace-nowrap ${
                countdownDetails.colorClass === 'green' ? 'bg-green-600 hover:bg-green-700' :
                countdownDetails.colorClass === 'yellow' ? 'bg-yellow-600 hover:bg-yellow-700' :
                'bg-red-600 hover:bg-red-700'
              }`}
              aria-label="Respond to slot offer"
            >
              Respond
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Notes */}
      {entry.notes && (
        <div className="text-sm text-gray-600 mb-3 p-2 bg-gray-50 rounded border border-gray-200">
          <span className="font-medium">Notes: </span>
          {entry.notes}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Actions */}
      {(entry.status === 'waiting' || entry.status === 'notified') && (
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
          {!showConfirm ? (
            <button
              onClick={() => setShowConfirm(true)}
              disabled={isRemoving}
              className="text-sm text-red-600 hover:text-red-700 font-medium"
            >
              Leave Waitlist
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Are you sure?</span>
              <button
                onClick={() => setShowConfirm(false)}
                className="text-sm text-gray-600 hover:text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleLeave}
                disabled={isRemoving}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
              >
                {isRemoving ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Removing...
                  </>
                ) : (
                  <>
                    <X className="w-3 h-3" />
                    Confirm
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Converted/Expired/Cancelled status */}
      {(entry.status === 'converted' || entry.status === 'expired' || entry.status === 'cancelled') && (
        <div className="pt-2 border-t border-gray-200 text-xs text-gray-500">
          {entry.status === 'converted' && entry.converted_at && (
            <p>Converted on {formatDate(entry.converted_at)}</p>
          )}
          {entry.status === 'cancelled' && entry.cancelled_at && (
            <p>Cancelled on {formatDate(entry.cancelled_at)}</p>
          )}
          {entry.status === 'expired' && (
            <p>Offer expired</p>
          )}
        </div>
      )}
    </div>
  );
}

export default React.memo(WaitlistCard);
